import type { StaffStatAccessEntry } from '~/hooks/queries/useStaffStatsAccessQuery';
import { type AnalyticsReportingMode, usesAnalyticsStatsUi } from '~/utils/analyticsReportingMode';

export type AnalyticsAccessReportingWarning = {
  message: string;
};

type ReportingMismatch = 'legacy_with_limits' | 'new_without_limits';

export function clientHasAnalyticsStatLimits(stats: StaffStatAccessEntry[] | undefined): boolean {
  return (stats ?? []).some((stat) => stat.hasLimit);
}

function getReportingMismatch(
  reportingMode: AnalyticsReportingMode,
  stats: StaffStatAccessEntry[] | undefined,
): ReportingMismatch | null {
  const hasLimits = clientHasAnalyticsStatLimits(stats);

  if (reportingMode === 'legacy' && hasLimits) {
    return 'legacy_with_limits';
  }

  if (usesAnalyticsStatsUi(reportingMode) && !hasLimits) {
    return 'new_without_limits';
  }

  return null;
}

const CURRENT_MISMATCH_MESSAGES: Record<ReportingMismatch, string> = {
  legacy_with_limits:
    'Classic Genie client, but Genie 2.0 limits are saved. They apply only after you switch reporting mode.',
  new_without_limits:
    'Genie 2.0 client with no limits yet. Set limits below before they can use analytics stats.',
};

const AFTER_CHANGE_MISMATCH_MESSAGES: Record<ReportingMismatch, string> = {
  legacy_with_limits:
    'Client will use Classic Genie. Saved Genie 2.0 limits stay on file until you switch back.',
  new_without_limits:
    'Client will use Genie 2.0. Set limits below or they cannot use analytics stats yet.',
};

function buildReportingWarning(
  mismatch: ReportingMismatch | null,
  messages: Record<ReportingMismatch, string>,
): AnalyticsAccessReportingWarning | null {
  if (!mismatch) return null;
  return { message: messages[mismatch] };
}

export function getAnalyticsAccessReportingWarning(
  reportingMode: AnalyticsReportingMode,
  stats: StaffStatAccessEntry[] | undefined,
): AnalyticsAccessReportingWarning | null {
  return buildReportingWarning(
    getReportingMismatch(reportingMode, stats),
    CURRENT_MISMATCH_MESSAGES,
  );
}

/**
 * After-change warning for go-live / switch-to-old-UI.
 * Do not use current-universe stats to infer destination when flipping v2 ↔ v2_legacy —
 * those rows belong to the current dataset; rely on wipe confirm + invalidated instead.
 */
export function getAnalyticsAccessReportingWarningAfterChange(
  from: AnalyticsReportingMode,
  to: AnalyticsReportingMode,
  stats: StaffStatAccessEntry[] | undefined,
): AnalyticsAccessReportingWarning | null {
  if (usesAnalyticsStatsUi(from) && usesAnalyticsStatsUi(to) && from !== to) {
    return null;
  }
  return buildReportingWarning(getReportingMismatch(to, stats), AFTER_CHANGE_MISMATCH_MESSAGES);
}

export function getParentChildReportingModeIncompatibilityWarning(
  parentMode: AnalyticsReportingMode | null | undefined,
  childMode: AnalyticsReportingMode | null | undefined,
): AnalyticsAccessReportingWarning | null {
  if (parentMode == null || childMode == null) {
    return null;
  }
  if (parentMode === childMode) {
    return null;
  }
  return {
    message:
      'Child stat set doesn’t match parent. Align reporting mode when you can. You can still save.',
  };
}
